import { MutationFunction, useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { WithReactQueryOptions } from 'ComponentLibrary'

export const useUploadMultiple = <T, TResult extends object = { status: string }>({
    onSuccess,
    onError,
    mutationFn,
    errorMsgFn,
}: WithReactQueryOptions<
    {
        mutationFn: MutationFunction<TResult, T>
        errorMsgFn: (data: T) => string
    },
    TResult
>) => {
    const [numToUpload, setNumToUpload] = useState(0)
    const [uploaded, setUploaded] = useState(0)
    const [failedUploads, setFailedUploads] = useState<string[]>([])

    const mutation = useMutation({
        mutationFn,
        onSuccess: (data) => {
            onSuccess?.(data)
            setUploaded((prev) => prev + 1)
        },
        onError,
    })

    const uploadMultiple = async (uploadData: T[]) => {
        const numberOfElements = uploadData.length
        setNumToUpload(numberOfElements)
        setUploaded(0)
        setFailedUploads([])

        const results = await Promise.allSettled(
            uploadData.map(async (data) => {
                try {
                    const result = await mutation.mutateAsync(data)
                    if ('status' in result && result.status === 'rejected') {
                        return Promise.reject(new Error(errorMsgFn(data)))
                    }
                    return Promise.resolve()
                } catch {
                    return Promise.reject(new Error(errorMsgFn(data)))
                }
            })
        )

        setFailedUploads(
            results.filter((result) => result.status === 'rejected').map((result) => result.status === 'rejected' && result.reason.message)
        )
    }

    return {
        isUploading: numToUpload - uploaded - failedUploads.length > 0 || mutation.isPending,
        isUploadError: mutation.isError,
        uploadError: mutation.error,
        upload: mutation.mutate,
        uploadMultiple,
        numToUpload: numToUpload - uploaded - failedUploads.length,
        uploaded,
        failedUploads,
    }
}
